import { IconLogout, IconShieldCheck, IconUser } from "@tabler/icons-react";
import { Link, useLocation } from "react-router";
import { breadcrumbFor } from "../../app/navigation";
import { useLocaleSwitch, useT } from "../../i18n/provider";
import { useCapabilities, useSession } from "../../lib/session";
import { IconButton } from "../kit";
import { cx, ICON_STROKE } from "../../lib/cx";
import { useVocabulary } from "../../i18n/vocabulary";

/**
 * The command bar — docs/24 §5. **48px, opaque.**
 *
 * Microsoft's title bar is 32px standard and "48px if it contains a search box or
 * a person-picture control"; this one carries a breadcrumb, the locale switch and
 * the account control.
 *
 * **No backdrop blur.** That is acrylic on a persistent surface, and the guidance
 * refuses it twice: acrylic is "only for transient, light-dismiss surfaces", and
 * "don't apply backdrop material to a UI element". §9.4 keeps acrylic for menus
 * and tooltips.
 */
export function CommandBar() {
  const { i18n } = useT();
  const vocab = useVocabulary();
  const [locale, setLocale] = useLocaleSwitch();
  const { pathname } = useLocation();
  const { state, signOut } = useSession();
  const { passkeyBacked } = useCapabilities();
  const trail = breadcrumbFor(pathname);

  return (
    <header
      data-command-bar
      className="no-print flex h-12 shrink-0 items-center gap-3 border-b border-border bg-background px-3"
    >
      {/* The breadcrumb carries the page's name — which is why a register that
          IS the page draws no heading (§5.1). */}
      <nav aria-label={i18n._("Breadcrumb")} className="flex min-w-0 items-center gap-1 text-sm">
        <Link to="/" className="shrink-0 font-semibold text-foreground outline-none">
          Shoppy
        </Link>
        {trail.map((crumb, index) => (
          <span key={crumb.path + index} className="flex min-w-0 items-center gap-1">
            <span aria-hidden className="text-muted-foreground">
              /
            </span>
            {index === trail.length - 1 ? (
              <span aria-current="page" className="truncate text-foreground">
                {vocab.nav(crumb.label)}
              </span>
            ) : (
              <Link to={crumb.path} className="truncate text-muted-foreground outline-none hover:text-foreground">
                {vocab.nav(crumb.label)}
              </Link>
            )}
          </span>
        ))}
      </nav>

      <span className="flex-1" />

      {/* The locale switch. A grid's `localeText` is `@initial`, so the registers
          remount on a change (§6.8) — the switch is a toggle, not a menu, because
          there are exactly two. */}
      <div role="group" aria-label={i18n._("Language")} className="flex items-center">
        {(["mn", "en"] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setLocale(option)}
            aria-pressed={locale === option}
            className={cx(
              "h-8 px-2 text-sm outline-none",
              option === "mn" ? "rounded-l-md border-r-0" : "rounded-r-md",
              "control",
              locale === option ? "text-foreground font-semibold" : "text-muted-foreground",
            )}
          >
            {option === "mn" ? "МН" : "EN"}
          </button>
        ))}
      </div>

      {state.status === "ready" && (
        <div className="flex items-center gap-2">
          {/* The session's own credential, stated rather than assumed. ADR-0013
              §2 requires a passkey for ADMIN and FINANCE, and an OTP-backed
              enrolment session legitimately exists — the operator should be able
              to see which one they are holding. */}
          <span
            className={cx(
              "hidden items-center gap-1.5 text-xs sm:inline-flex",
              passkeyBacked ? "text-success" : "text-caution",
            )}
          >
            {passkeyBacked ? (
              <IconShieldCheck size={16} stroke={ICON_STROKE} aria-hidden />
            ) : (
              <IconUser size={16} stroke={ICON_STROKE} aria-hidden />
            )}
            {passkeyBacked ? i18n._("Passkey session") : i18n._("Enrolment session")}
          </span>
          <span className="max-w-40 truncate text-sm text-muted-foreground">
            {state.me.account.displayName ?? state.me.account.phone}
          </span>
          <IconButton label={i18n._("Sign out")} icon={IconLogout} onClick={() => void signOut()} />
        </div>
      )}
    </header>
  );
}
